// The 訂貨 screen's own arithmetic: what the owner actually decided to order, and the two honest
// admissions the engine now makes (styles with no source shop, and a 新季款 lane it could not fill).
// Split out of format.ts to keep both files readable — nothing here touches the 今天 walk.
import { displayTitle, monthDay, orderPieces, tierLines, tierTotal, variantLabel } from './format';
import { listVariants } from './counterList';
import { isWarehouseLane, sourcingBucket } from './sourcing';
import { WALK_ORDER } from './tokens';
import {
  RestockV2Autumn, RestockV2Blocked, RestockV2Excluded, RestockV2Line, RestockV2ListVariant, RestockV2Order,
  RestockV2OrderGroup, RestockV2OrderLine,
  RestockV2OrderVariant, RestockV2OrderedLine, RestockV2Plan, RestockV2Unaccounted, RestockV2Unlinked,
  TierKey,
} from './types';

export function isExcluded(excluded: RestockV2Excluded | undefined, sku: string): boolean {
  return !!excluded && excluded[sku] === true;
}

// ---- excluding a line from the plan ---------------------------------------------------------
// Magnus 2026-09-15: 「I should be able to remove an item from the plan if I want to?」 — the
// engine proposes, the owner decides. An unticked SKU leaves the copied order, the tier totals and
// the /ordered payload, but stays on screen (dimmed) so the decision is visible and reversible.

export function excludedCount(excluded?: RestockV2Excluded): number {
  return Object.keys(excluded || {}).length;
}

export function includedLines(
  lines: RestockV2OrderLine[] | undefined, excluded?: RestockV2Excluded,
): RestockV2OrderLine[] {
  return (lines || []).filter((line) => !isExcluded(excluded, line.sku));
}

// Every Korea line the 訂貨 screen can tick, in group order — the population the header counts.
export function koreaOrderLines(order: RestockV2Order | undefined): RestockV2OrderLine[] {
  const out: RestockV2OrderLine[] = [];
  (order?.groups || []).forEach((group) => out.push(...(group.lines || [])));
  return out;
}

export interface RestockV2Selection {
  styles: number;
  pieces: number;
  totalStyles: number;
  totalPieces: number;
}

// 「已選 N 款 / M 件」 — counted over the Korea groups, which is the order being placed.
export function selectionSummary(order: RestockV2Order | undefined, excluded?: RestockV2Excluded): RestockV2Selection {
  const all = koreaOrderLines(order);
  const kept = includedLines(all, excluded);
  return { styles: kept.length, pieces: orderPieces(kept), totalStyles: all.length, totalPieces: orderPieces(all) };
}

// With nothing unticked the backend's own totals are authoritative; once something is, the tier
// tile has to show what the buyer will actually order, so it is summed from the kept lines.
export function tierTotalKept(
  order: RestockV2Order | undefined, tier: TierKey, excluded?: RestockV2Excluded,
): number {
  if (!excludedCount(excluded)) return tierTotal(order?.totals, tier);
  return orderPieces(includedLines(tierLines(order, tier), excluded));
}

export function koreaTotalKept(order: RestockV2Order | undefined, excluded?: RestockV2Excluded): number {
  if (!excludedCount(excluded)) return Number(order?.totals?.korea ?? 0) || 0;
  return orderPieces(includedLines(koreaOrderLines(order), excluded));
}

export function groupPiecesKept(group: RestockV2OrderGroup, excluded?: RestockV2Excluded): number {
  if (!excludedCount(excluded)) return Number(group.pieces ?? 0) || 0;
  return orderPieces(includedLines(group.lines, excluded));
}

// Same shape as the endpoint's own copy_text (noke-restock-v2-rules.copyText), rebuilt locally
// because the text the buyer pastes into LINE has to drop the lines he unticked. Untouched plans
// keep handing over the server's string verbatim — see orderCopyText.
function copyChip(variant: RestockV2OrderVariant): string {
  return `${variantLabel(variant.color, variant.size) || '單一款'} ×${Number(variant.order_qty) || 0}`;
}

// ---- the shop heading, which is a heading and nothing else ------------------------------------
// Magnus 2026-09-18: 「tag doesn't matter, there is order links」. A 進貨店家 tag says which Sinsang
// shop's lines sit together so 批次開啟 can open them in one go. A line without one is a perfectly
// normal order line; it just has no shop to be opened alongside, and it groups under this heading
// (backend noke-restock-v2-rules.js:37 `UNKNOWN_SOURCE`). It is a LABEL, never a warning.
export const NO_SHOP_GROUP = '沒有分店家';
// What the same placeholder was called before 2026-09-18. Still recognised, so a plan served mid
// deploy cannot get its placeholder written into /ordered as if it were a real shop name.
const LEGACY_NO_SHOP_GROUP = '未標記來源';

/** The tag when it NAMES A SHOP, else null — so no caller can pass a placeholder off as a supplier. */
export function shopTag(tag?: string | null): string | null {
  const value = String(tag ?? '').trim();
  if (!value || value === NO_SHOP_GROUP || value === LEGACY_NO_SHOP_GROUP) return null;
  return value;
}

// ---- 訂貨 as ONE flat list, with the shop demoted to a chip -----------------------------------
// Magnus 2026-09-19: 「don't need to order by tag name」 — said after asking twice why a SKU 「wasn't
// in 訂貨」 when both times it was, buried under a shop heading. Measured on his live plan that day:
// 20 shop groups for 23 lines, SEVENTEEN of them holding exactly one line, so finding SKU 2979 meant
// scrolling past twenty headings (it sat at group #11) each wearing its own 「批次開啟 Sinsang・1 個」.
// The shop is still a fact about the line; it just stops being the SPINE of the screen.
//
// THE COPY TEXT STAYS GROUPED BY 【shop】 (buildOrderCopyText above) ON PURPOSE. He places the order
// on Sinsang shop by shop, so the text he pastes and the screen he reads are shaped for two
// different jobs. Do not "fix" one to match the other.

export interface RestockV2FlatLine {
  line: RestockV2OrderLine;
  /** The shop that groups the line, or null when it has none — a LABEL, never a warning. */
  shop: string | null;
}

// 主力 → 新季款 → 線索款: the order he thinks in, must-restock first (Magnus 2026-09-19).
// `seasonal` is the v2 spelling of 新季款 and ranks with it.
const TIER_RANK: Record<string, number> = { core: 0, new_season: 1, seasonal: 1, lead: 2 };

// Within a tier, 「then by category」. Categories are ordered by the RAIL they hang on
// (tokens.WALK_ORDER via sourcing.sourcingBucket, which mirrors the backend's DEFAULT_FIXTURES)
// rather than by the category string, because that is the only category ordering this codebase
// actually owns — and it keeps 短袖／長袖／針織／外套 together instead of scattering them around
// 短褲 the way a code-point sort does. Same-category lines still land adjacent either way.
function rackRank(cat?: string | null): number {
  const index = WALK_ORDER.indexOf(sourcingBucket(cat));
  return index < 0 ? WALK_ORDER.length : index;
}

const NUMERIC_SKU = /^\d+$/;

/** Numeric SKUs ascending (3591 before 3601, never "3591" < "359"), then the M-prefixed ones. */
function compareSku(a: string, b: string): number {
  const na = NUMERIC_SKU.test(a);
  const nb = NUMERIC_SKU.test(b);
  if (na && nb) return Number(a) - Number(b);
  if (na !== nb) return na ? -1 : 1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function compareOrderLines(a: RestockV2FlatLine, b: RestockV2FlatLine): number {
  const tierA = TIER_RANK[a.line.tier] ?? 9;
  const tierB = TIER_RANK[b.line.tier] ?? 9;
  if (tierA !== tierB) return tierA - tierB;
  const rackA = rackRank(a.line.cat);
  const rackB = rackRank(b.line.cat);
  if (rackA !== rackB) return rackA - rackB;
  const catA = String(a.line.cat ?? '').trim();
  const catB = String(b.line.cat ?? '').trim();
  if (catA !== catB) return catA < catB ? -1 : 1;
  return compareSku(a.line.sku, b.line.sku);
}

/**
 * Every Korea order line, ONCE, in reading order — no shop headings.
 *
 * The shop is read off the line and falls back to its group's tag, the same hedge
 * `koreaOrderedLines` makes: the backend stamps the group heading onto the line, so the placeholder
 * has to be filtered by `shopTag` on both or a heading would be passed off as a supplier.
 */
export function flatOrderLines(order?: RestockV2Order): RestockV2FlatLine[] {
  const rows: RestockV2FlatLine[] = [];
  (order?.groups || []).forEach((group) => {
    const groupShop = shopTag(group.source_tag);
    (group.lines || []).forEach((line) => rows.push({ line, shop: shopTag(line.source_tag) ?? groupShop }));
  });
  return rows.sort(compareOrderLines);
}

/** The order's shops, first-seen first. The placeholder heading is not a shop and is not here. */
export function orderShopTags(order?: RestockV2Order): string[] {
  const seen: string[] = [];
  (order?.groups || []).forEach((group) => {
    const shop = shopTag(group.source_tag);
    if (shop && seen.indexOf(shop) < 0) seen.push(shop);
  });
  return seen;
}

// ---- the 進貨店家 tags that have a per-product NOTE typed into them ----------------------------
// Six live tags carry one: 「miller 薄荷綠是淺的 不是深的」「guyot mocha 是 深棕」「aden 注意是否高領」
// 「rex 檢查肩線」「5008 bric aid」「avec design」 — and guyot is consequently TWO tags, so the same
// shop shows up as two different chips. The note is useful (it says what to check), so nothing here
// truncates it, splits it or regroups on it: the chip prints the tag verbatim and this only names
// the tags so whoever owns the data can move the note to where notes belong.
//
// DETECTION, not parsing. A tag is flagged when it contains Han characters (Sinsang shop names are
// latin/digits: deefhouse, snatch, lcod, guyot, miller, aden, rex) or when it is another tag of this
// same order plus more words (「guyot mocha 是 深棕」 vs 「guyot」). Both are compared against real
// tags; neither ever rewrites one, so a genuine two-word name like 「avec design」 is left alone.
// 「5008 bric aid」 is a deliberate false NEGATIVE — a missed hint costs nothing, a wrong split does.
const HAN = new RegExp('[\u3400-\u4DBF\u4E00-\u9FFF]');

export function shopNoteTags(order?: RestockV2Order): string[] {
  const tags = orderShopTags(order);
  return tags.filter((tag) => HAN.test(tag)
    || tags.some((other) => other !== tag && tag.indexOf(`${other} `) === 0));
}

export const SHOP_NOTE_HINT = '這些進貨店家標籤裡打了單品備註，所以同一家店會拆成兩種標籤。備註有用（它寫了要檢查什麼），這裡照原字顯示、不會改它；要整理的話把備註移到商品備註欄，標籤只留店名：';

function copyRow(line: RestockV2OrderLine): string {
  const chips = (line.variants || []).filter((variant) => (Number(variant.order_qty) || 0) > 0).map(copyChip);
  return chips.length ? `${line.sku} ${displayTitle(line.title)}｜${chips.join('、')}` : '';
}

export function buildOrderCopyText(plan: RestockV2Plan | null, excluded?: RestockV2Excluded): string {
  const order = plan?.order;
  const groups = order?.groups || [];
  const kept = includedLines(koreaOrderLines(order), excluded);
  const out = [`NOKE 韓國訂單 ${monthDay(plan?.meta?.next_order_night)}（${orderPieces(kept)} 件）`];
  groups.forEach((group) => {
    const rows = includedLines(group.lines, excluded).map(copyRow).filter((row) => !!row);
    if (!rows.length) return;
    out.push('', `【${group.source_tag || NO_SHOP_GROUP}】`, ...rows);
  });
  const warehouse = includedLines(order?.warehouse, excluded).map(copyRow).filter((row) => !!row);
  if (warehouse.length) out.push('', `倉庫另補：${warehouse.join('、')}`);
  return out.join('\n');
}

export function orderCopyText(plan: RestockV2Plan | null, excluded?: RestockV2Excluded): string {
  if (!excludedCount(excluded)) return plan?.order?.copy_text || '';
  return buildOrderCopyText(plan, excluded);
}

// ---- the lines nobody can order -------------------------------------------------------------
// What actually stops an order is a missing 下單連結 — a `product_links` row — not a missing
// 進貨店家 tag (Magnus 2026-09-18). `unlinked` is the backend's true name for this bucket;
// `unsourced` is the same array under the name the shipped screen read, kept for one release.
// Both are handled here so the screen is right whichever one arrives.
// 台灣倉庫 rows are dropped HERE rather than in the section, because this is the one door every
// count and every card comes through. MdoC is stock Magnus already owns in Taiwan; it is never
// ordered from Korea, so it needs no 下單連結 and 訂不到 is simply false about it. The backend agrees
// (its own bucket is `!o.warehouse && no_order_link`), so this filter should never fire — and if a
// stale plan ever makes it fire, it costs one row on screen and saves him another false alarm.
export function unlinkedRows(order: RestockV2Order | undefined): RestockV2Unlinked[] {
  return (order?.unlinked || order?.unsourced || []).filter((row) => !isWarehouseLane(row));
}

export function unlinkedPieces(order: RestockV2Order | undefined): number {
  const rows = unlinkedRows(order);
  if (!rows.length) return 0;
  const served = (order?.unlinked || order?.unsourced || []).length;
  const totals = Number(order?.totals?.unlinked ?? order?.totals?.unsourced);
  // The server's total counts the rows the server sent. Once this has dropped one it no longer
  // describes what is on screen, so the pieces are summed from the rows actually drawn.
  if (rows.length === served && Number.isFinite(totals) && totals > 0) return totals;
  return rows.reduce((sum, row) => sum + (Number(row.order_qty_would_be) || 0), 0);
}

// No destination is named on purpose: the link is stored where links are stored, and pointing at a
// screen that may not be the right one would be worse than naming the fix. The old sentence sent
// him to 「進貨／標籤」 to fix a tag, which would not have made a single one of these orderable.
export const UNLINKED_NOTE = '這些款沒有下單連結，所以沒有人訂得到。補上下單連結，下次計算就會自動進訂單。';

// ---- the styles nobody can order, colour by colour -------------------------------------------
export function blockedRows(order: RestockV2Order | undefined): RestockV2Blocked[] {
  return order?.blocked || [];
}

export function blockedPieces(order: RestockV2Order | undefined): number {
  const rows = blockedRows(order);
  if (!rows.length) return 0;
  const totals = Number(order?.totals?.blocked);
  if (Number.isFinite(totals) && totals > 0) return totals;
  return rows.reduce((sum, row) => sum + (Number(row.order_qty_would_be) || 0), 0);
}

export const BLOCKED_NOTE = '這些款在清單上，但每個顏色現在都訂不到（停售、下架或常缺貨）。把色碼恢復或換顏色，下次計算就會自動進訂單。';

// ---- the lines NO list claimed ---------------------------------------------------------------
// The coverage ledger (backend noke-restock-v2-reconcile.js). Reading it costs nothing on a
// healthy plan, and it is the one thing standing between a future gate and another silent drop.
export function unaccountedRows(order: RestockV2Order | undefined): RestockV2Unaccounted[] {
  return order?.unaccounted || [];
}

export function unaccountedPieces(order: RestockV2Order | undefined): number {
  const rows = unaccountedRows(order);
  if (!rows.length) return 0;
  const totals = Number(order?.totals?.unaccounted);
  if (Number.isFinite(totals) && totals > 0) return totals;
  return rows.reduce((sum, row) => sum + (Number(row.pieces) || 0), 0);
}

export const UNACCOUNTED_NOTE = '這些款有目標件數，但沒有進到訂貨、去拿、上架、帶回或等到貨任何一張清單。這是系統的問題，不是你的操作，請把 SKU 回報。';

// ---- the lane the engine could not fill ------------------------------------------------------
// 「秋新款只找到 20 款（目標 30 款）：符合條件的秋新款只有 20 款」. Empty when the lane was full,
// so the screen never claims a shortfall that did not happen.
export function laneShortLine(autumn?: RestockV2Autumn | null): string {
  const short = autumn?.lane_short;
  if (!short) return '';
  const admitted = Number(short.admitted);
  const asked = Number(short.asked_for);
  if (!Number.isFinite(admitted) || !Number.isFinite(asked)) return '';
  const head = `秋新款只找到 ${admitted} 款（目標 ${asked} 款）`;
  const reason = (short.reason || '').trim();
  return reason ? `${head}：${reason}` : head;
}

// ---- 加顏色／尺寸: the colours the engine did not put on the line -----------------------------
// Magnus 2026-09-18: 「there is still no way to edit to a specific size and 尺寸 that wasn't shown」.
//
// WHERE THE FULL LIST HONESTLY LIVES. An order line's `variants[]` is NOT the style's colours — the
// backend pushes a row onto it only where there is a GAP to fill
// (noke-restock-v2-plan.js:196, `target − on_hand − transit − ordered > 0`), so an XL the counter
// already covers, and any colour the engine gave target 0, are absent by construction. The complete
// per-SKU variant map is built once in noke-restock-v2-select.js:53-70 from the CATALOGUE rows, and
// the one place it reaches the browser is `plan.list.approved[].variants` (+ `vetoed_variants`) —
// noke-restock-v2-list.js:339 emits every one of them with `target`, `on_hand`, `orderable` and
// `block_reason`. That is the source used here. Nothing is synthesised: a SKU with no approved row
// (a backend without `noke_counter_list`) offers NO picker rather than a guessed list.
//
// KNOWN GAP, deliberately not papered over: `liveVariants` does not send per-variant `office` or
// `transit`. Where another surface of the SAME plan carries a measured figure for that exact
// sku+variant (a 今天 row, a rack tile, another order line) it is read from there; where nothing
// measured it, the row simply says nothing about the office instead of printing a zero.

export type AddableState = 'ready' | 'blocked' | 'vetoed';

export interface RestockV2Addable {
  variant_id: number;
  color: string | null;
  size: string | null;
  target: number;
  on_hand: number | null;
  office: number | null;
  transit: number | null;
  state: AddableState;
  // zh-TW, rendered verbatim. '' exactly when `state === 'ready'`.
  reason: string;
}

export interface VariantStock {
  on_hand: number | null;
  office: number | null;
  transit: number | null;
}

const EMPTY_STOCK: VariantStock = { on_hand: null, office: null, transit: null };

export const ADD_VARIANT_LABEL = '加顏色／尺寸';
export const ADD_VARIANT_TITLE = '引擎沒排的顏色／尺寸';
export const ADD_VARIANT_NOTE = '加進來的顏色從 0 件開始，跟其他顏色一樣用 −／＋ 調。';
export const ADD_VARIANT_EMPTY = '這款的顏色／尺寸都已經在上面了。';
export const ADD_VARIANT_NO_LIST = '拿不到這款的完整顏色／尺寸（選品清單還沒建立），只能調上面這幾個。';
export const ADD_VARIANT_BLOCKED = '不能訂';
export const ADD_VARIANT_VETOED = '已排除';
export const ADD_VARIANT_VETOED_REASON = '你在選品把這個顏色／尺寸排除了，要先「加回」才能訂';
export const ADD_VARIANT_UNORDERABLE = '目前不能訂';

const finite = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const stockKey = (sku: string, variantId?: number | null): string => `${sku}|${variantId ?? ''}`;

/**
 * `sku|variant_id` → whatever this plan measured for that exact pair, wherever it said it.
 *
 * First measured value per field wins; a field nothing measured stays null and is NOT rendered.
 * Null and 0 are different claims here — 「沒有量到辦公室有幾件」 is not 「辦公室 0 件」.
 */
export function variantStockIndex(plan: RestockV2Plan | null | undefined): Record<string, VariantStock> {
  const out: Record<string, VariantStock> = {};
  const add = (sku: string, variantId: number | null | undefined, row: VariantStock) => {
    if (!sku || variantId === null || variantId === undefined) return;
    const key = stockKey(sku, variantId);
    const current = out[key] || EMPTY_STOCK;
    out[key] = {
      on_hand: current.on_hand ?? row.on_hand,
      office: current.office ?? row.office,
      transit: current.transit ?? row.transit,
    };
  };
  const fromLine = (sku: string, row: RestockV2Line | RestockV2OrderVariant, variantId: number | null | undefined) => {
    add(sku, variantId, {
      on_hand: finite(row.on_hand), office: finite(row.office), transit: finite(row.transit),
    });
  };
  const orderLines: RestockV2OrderLine[] = [
    ...koreaOrderLines(plan?.order), ...(plan?.order?.warehouse || []),
  ];
  orderLines.forEach((line) => (line.variants || [])
    .forEach((variant) => fromLine(line.sku, variant, variant.variant_id)));
  (['display', 'from_office', 'bring_back', 'waiting'] as const).forEach((group) => {
    (plan?.today?.[group] || []).forEach((line) => fromLine(line.sku, line, line.variant_id));
  });
  (plan?.racks?.fixtures || []).forEach((fixture) => (fixture.tiles || []).forEach((tile) => add(
    tile.sku, tile.variant_id,
    { on_hand: finite(tile.on_hand), office: finite(tile.office), transit: finite(tile.transit) },
  )));
  return out;
}

/** SKU → every catalogue colour/size the plan knows about, vetoed ones included and flagged. */
export function catalogVariantIndex(
  plan: RestockV2Plan | null | undefined,
): Record<string, RestockV2ListVariant[]> {
  const out: Record<string, RestockV2ListVariant[]> = {};
  (plan?.list?.approved || []).forEach((row) => {
    if (!row || !row.sku) return;
    out[row.sku] = listVariants(row);
  });
  return out;
}

function addableState(variant: RestockV2ListVariant): { state: AddableState; reason: string } {
  if (variant.vetoed === true) return { state: 'vetoed', reason: ADD_VARIANT_VETOED_REASON };
  const blocked = (variant.block_reason || '').trim();
  if (blocked) return { state: 'blocked', reason: blocked };
  // `orderable: false` with no sentence should not happen (noke-restock-v2-select.js always pairs
  // the two), but an unorderable row offered as orderable is the one failure this must not have.
  if (variant.orderable === false) return { state: 'blocked', reason: ADD_VARIANT_UNORDERABLE };
  return { state: 'ready', reason: '' };
}

/**
 * The picker's rows for ONE line: every catalogue colour/size that is not already a stepper on it.
 *
 * `line` is the EDITED line, so a colour he has already added is not offered twice, and one the
 * engine picked up on a later recompute drops out of the picker by itself.
 */
export function addableVariants(
  line: RestockV2OrderLine,
  catalog: Record<string, RestockV2ListVariant[]>,
  stock: Record<string, VariantStock>,
): RestockV2Addable[] {
  const already: Record<string, true> = {};
  (line.variants || []).forEach((variant) => {
    if (variant.variant_id !== null && variant.variant_id !== undefined) {
      already[String(variant.variant_id)] = true;
    }
  });
  return (catalog[line.sku] || [])
    .filter((variant) => variant.variant_id !== null && variant.variant_id !== undefined)
    .filter((variant) => !already[String(variant.variant_id)])
    .map((variant) => {
      const measured = stock[stockKey(line.sku, variant.variant_id)] || EMPTY_STOCK;
      return {
        variant_id: variant.variant_id,
        color: variant.color ?? null,
        size: variant.size ?? null,
        target: finite(variant.target) ?? 0,
        on_hand: finite(variant.on_hand) ?? measured.on_hand,
        office: finite(variant.office) ?? measured.office,
        transit: finite(variant.transit) ?? measured.transit,
        ...addableState(variant),
      };
    });
}

export function addableIsOrderable(row: RestockV2Addable): boolean {
  return row.state === 'ready';
}

/** 「在櫃 2 件 · 辦公室 1 件 · 目標 2 件」 — only what this plan actually measured. */
export function addableStockLine(row: RestockV2Addable): string {
  const parts: string[] = [];
  // 在櫃 is printed even at 0: 「是不是門市已經有了」 is the question the row exists to answer.
  if (row.on_hand !== null) parts.push(`在櫃 ${row.on_hand} 件`);
  if (row.office !== null && row.office > 0) parts.push(`辦公室 ${row.office} 件`);
  if (row.transit !== null && row.transit > 0) parts.push(`在途 ${row.transit} 件`);
  parts.push(row.target > 0 ? `目標 ${row.target} 件` : '引擎沒排這個');
  return parts.join(' · ');
}

/**
 * 「這款辦公室還有 3 件」 — the STYLE's own owned pieces, said as the style's, because that is the
 * grain the endpoint measures them at (`evidenceOf` → `ownedStock`). Empty when it owns none.
 */
export function styleOwnedLine(plan: RestockV2Plan | null | undefined, sku: string): string {
  const row = (plan?.list?.approved || []).filter((item) => item.sku === sku)[0];
  if (!row) return '';
  const shelf = finite(row.not_displayed) || 0;
  const office = finite(row.office_qty) || 0;
  const parts: string[] = [];
  if (office > 0) parts.push(`辦公室 ${office} 件`);
  if (shelf > 0) parts.push(`門市未展示 ${shelf} 件`);
  return parts.length ? `這款：${parts.join(' · ')}` : '';
}

/** The order row a picked colour becomes. `order_qty` 0 — the engine chose nothing for it. */
export function addableOrderVariant(row: RestockV2Addable): RestockV2OrderVariant {
  return {
    variant_id: row.variant_id,
    color: row.color,
    size: row.size,
    on_hand: row.on_hand ?? 0,
    office: row.office ?? 0,
    transit: row.transit ?? 0,
    ordered: 0,
    target: row.target,
    order_qty: 0,
  };
}

// ---- the /ordered payload -------------------------------------------------------------------
// Every Korea variant with something to order, flattened into the /ordered payload. A SKU the
// owner unticked never reaches the payload — 「記為在途」 must record what he actually ordered.
export function koreaOrderedLines(plan: RestockV2Plan | null, excluded?: RestockV2Excluded): RestockV2OrderedLine[] {
  const out: RestockV2OrderedLine[] = [];
  (plan?.order?.groups || []).forEach((group) => {
    (group.lines || []).forEach((line) => {
      if (isExcluded(excluded, line.sku)) return;
      (line.variants || []).forEach((variant) => {
        if ((Number(variant.order_qty) || 0) <= 0) return;
        out.push({
          variant_id: variant.variant_id ?? null, sku: line.sku, color: variant.color ?? null,
          size: variant.size ?? null, qty: Number(variant.order_qty), tier: line.tier,
          // The SHOP, or null — `shopTag` filters the group placeholder out of BOTH fields. The
          // line carries the heading too (the backend stamps 沒有分店家 onto it), so reading
          // `line.source_tag` raw would have written the placeholder into /ordered as a supplier.
          source_tag: shopTag(line.source_tag) ?? shopTag(group.source_tag),
        });
      });
    });
  });
  return out;
}
